// Ce qu'une candidature attend de nous, déduit de son étape et de sa date
// (issue #79).
//
// DÉDUIT, JAMAIS STOCKÉ. Le statut vit dans le journal (`events`), et sa date
// avec lui (`statusAt`, dernier `candidature.status`). Une colonne « prochaine
// étape » serait une seconde vérité, à tenir à jour à chaque geste, et qui
// finirait par dire autre chose que l'étape affichée à côté.

import type { CandidatureStatus, SuiviRow } from './api'
import { parseDate } from './format'

/** La relance se propose une semaine après l'envoi (ou la relance d'avant). */
export const JOURS_AVANT_RELANCE = 7

export type ProchaineEtape =
  | { cle: 'envoyer' }
  | { cle: 'relancer'; date: Date; enRetard: boolean }
  | { cle: 'entretien' }

export function prochaineEtape(
  k: { status: CandidatureStatus; statusAt: string },
  maintenant: Date = new Date()
): ProchaineEtape | null {
  switch (k.status) {
    case 'a_envoyer':
      return { cle: 'envoyer' }
    case 'envoye':
    case 'relance': {
      const depuis = parseDate(k.statusAt) ?? maintenant
      const date = new Date(depuis.getTime() + JOURS_AVANT_RELANCE * 86400_000)
      return { cle: 'relancer', date, enRetard: date.getTime() <= maintenant.getTime() }
    }
    case 'entretien':
      return { cle: 'entretien' }
    default:
      // Placé ou refusé : plus rien à faire.
      return null
  }
}

/** Une offre disparue du site avant qu'on ait envoyé quoi que ce soit : il n'y
 *  a plus rien à envoyer, elle quitte le pipeline actif pour un repli. */
export const estExpireeNonEnvoyee = (k: { status: CandidatureStatus; offerArchived: boolean }): boolean =>
  k.offerArchived && k.status === 'a_envoyer'

/** Une candidature et le geste qu'elle attend maintenant. */
export interface ChoseAFaire {
  k: SuiviRow
  etape: ProchaineEtape
}

/**
 * Ce que l'accueil de Radar met sous « À faire » (issue #168) : les
 * candidatures dont l'étape suivante demande un geste AUJOURD'HUI. Envoyer
 * (sauf une offre disparue avant l'envoi), relancer une fois la date passée,
 * préparer l'entretien. Une relance pas encore due n'y est pas : elle n'attend
 * rien de personne. Dans l'ordre des gestes, les plus anciens d'abord, et
 * cinq au plus : au-delà, « Mes offres » est le bon écran.
 */
export function aFaire(ks: SuiviRow[], maintenant: Date = new Date(), max = 5): ChoseAFaire[] {
  const ordre = { envoyer: 0, relancer: 1, entretien: 2 } as const
  return ks
    .filter((k) => !estExpireeNonEnvoyee(k))
    .map((k) => ({ k, etape: prochaineEtape(k, maintenant) }))
    .filter((c): c is ChoseAFaire => !!c.etape && (c.etape.cle !== 'relancer' || c.etape.enRetard))
    .sort((a, b) => ordre[a.etape.cle] - ordre[b.etape.cle] || a.k.statusAt.localeCompare(b.k.statusAt))
    .slice(0, max)
}

/** Le rappel d'une offre « plus tard » (lot 3) : elle est citée par le récap
 *  du premier lundi qui suit d'une semaine la mise de côté. Même règle que
 *  `offresARappeler` (src/services/gardees.ts), lue ici pour l'annoncer. */
export const JOURS_RAPPEL = 7
export function dateRappel(miseDeCote: Date): Date {
  // Un jour de plus que la semaine : mise de côté un lundi à 10 h, elle n'a
  // pas sept jours au passage de la nuit du lundi suivant (02:00 UTC).
  const d = new Date(miseDeCote.getTime() + (JOURS_RAPPEL + 1) * 86400_000)
  const jour = d.getDay() // 0 dimanche, 1 lundi
  d.setDate(d.getDate() + ((8 - jour) % 7))
  return d
}
